"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useDailySeerTrend = void 0;
const react_1 = require("react");
const queries_1 = require("../../graphql/queries");
const amplifyBootstrap_1 = require("../../utils/amplifyBootstrap");
/**
 * Hook to fetch daily SEER/EER aggregates for a hub (optionally scoped to a single HVAC group)
 * over a date range, for use in trend charts.
 * @param hubId - Hub ID to fetch aggregates for
 * @param groupId - Optional HVAC group ID to scope results to a single system
 * @param startDate - Start date in YYYY-MM-DD format (Pacific Time)
 * @param endDate - End date in YYYY-MM-DD format (Pacific Time)
 * @returns Daily HVAC aggregates, a per-group SEER series map, loading state, error, and refetch
 */
const useDailySeerTrend = (hubId, groupId, startDate, endDate) => {
    const [data, setData] = (0, react_1.useState)(null);
    const [seriesByGroup, setSeriesByGroup] = (0, react_1.useState)(new Map());
    const [loading, setLoading] = (0, react_1.useState)(false);
    const [error, setError] = (0, react_1.useState)(null);
    const fetchDailySeerTrend = async () => {
        if (!hubId || !startDate || !endDate) {
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const response = await amplifyBootstrap_1.API.graphql({
                query: queries_1.getDailyHvacAggregates,
                variables: { hubId, groupId, startDate, endDate },
            });
            const rawData = response.data?.getDailyHvacAggregates || [];
            const sortedData = [...rawData].sort((a, b) => a.date.localeCompare(b.date));
            const grouped = new Map();
            sortedData.forEach((aggregate) => {
                const points = grouped.get(aggregate.groupId) ?? [];
                points.push({ date: aggregate.date, value: aggregate.avgSeerValue });
                grouped.set(aggregate.groupId, points);
            });
            setData(sortedData);
            setSeriesByGroup(grouped);
        }
        catch (err) {
            console.error('Error fetching daily SEER trend:', err);
            setError(err instanceof Error ? err.message : 'Failed to fetch daily SEER trend');
            setData(null);
            setSeriesByGroup(new Map());
        }
        finally {
            setLoading(false);
        }
    };
    const refetch = async () => {
        await fetchDailySeerTrend();
    };
    (0, react_1.useEffect)(() => {
        fetchDailySeerTrend();
    }, [hubId, groupId, startDate, endDate]);
    return {
        data,
        seriesByGroup,
        loading,
        error,
        refetch,
    };
};
exports.useDailySeerTrend = useDailySeerTrend;
exports.default = exports.useDailySeerTrend;
